/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import { FieldErrors, UseFormRegister } from 'react-hook-form'

type UiCheckboxProps = {
    name: string
    label?: React.ReactNode
    description?: React.ReactNode
    register?: UseFormRegister<any>
    errors?: FieldErrors
    required?: boolean
    checked?: boolean
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    disabled?: boolean
    className?: string
}

const UiCheckbox: React.FC<UiCheckboxProps> = ({
    name,
    label,
    description,
    register,
    errors,
    required,
    checked,
    onChange,
    disabled,
    className,
}) => {
    const hasError = Boolean(errors && errors[name])
    const message = hasError ? errors[name]?.message : undefined

    return (
        <div className={clsx('UiCheckbox w-full', className)}>
            <label
                className={clsx(
                    'flex items-start gap-3 text-gray-900 dark:text-gray-100'
                )}
            >
                <input
                    {...(register ? register(name, { required }) : {})}
                    name={name}
                    type={'checkbox'}
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                    className={clsx(
                        'h-4 w-4 rounded border-gray-300 text-primary-600 focus:border-primary-600',
                        'dark:border-gray-600 dark:bg-gray-900',
                        hasError &&
                            'border-red-300 ring-1 ring-red-500 dark:border-red-500 dark:ring-red-500'
                    )}
                />
                <span className={clsx('flex-1')}>
                    <span className={clsx('block text-sm font-medium')}>
                        {label}
                    </span>
                    {description && (
                        <span
                            className={clsx(
                                'block text-xs text-gray-500 dark:text-gray-400'
                            )}
                        >
                            {description}
                        </span>
                    )}
                </span>
            </label>
            {message && (
                <div className={clsx('mt-1 text-xs text-red-600')}>
                    {message as React.ReactNode}
                </div>
            )}
        </div>
    )
}

export default UiCheckbox
